import React, { useState, useEffect } from "react";
import {
  Search,
  GraduationCap,
  Calendar,
  Clock,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  BookOpen,
  Users,
  X,
  Eye,
  Building,
  Trash2,
  Download,
  Filter,
  Check,
  ChevronDown,
} from "lucide-react";
import WeeklyAvailabilityGrid from "./WeeklyAvailabilityGrid";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import Modal from "../../../components/ui/Modal";

interface Teacher {
  id: string;
  name: string;
  branch: string;
  availableHours: number;
  assignedLessons: number;
}

interface TimeSlot {
  id: string;
  period: string;
  startTime: string;
  endTime: string;
}

interface TeacherAvailability {
  teacherId: string;
  day: string;
  timeSlotId: string;
  isAvailable: boolean;
}

interface ClassInfo {
  id: string;
  name: string;
  grade: number;
  studentCount: number;
}

interface LessonAssignment {
  id: string;
  teacherId: string;
  classId: string;
  subject: string;
  day: string;
  timeSlotId: string;
  className: string;
  classGrade: number;
}

interface ClassScheduleEntry {
  id: string;
  classId: string;
  className: string;
  day: string;
  timeSlotId: string;
  subject: string;
  teacherId: string;
  teacherName: string;
}

interface ClassInfo {
  id: string;
  name: string;
  grade: number;
  studentCount: number;
}

interface ClassroomScheduleEntry {
  id: string;
  classroomId: string;
  classroomName: string;
  day: string;
  timeSlotId: string;
  subject: string;
  teacherId: string;
  teacherName: string;
  classId: string;
  className: string;
}

interface ClassroomInfo {
  id: string;
  name: string;
  capacity: number;
  type: string;
  equipment: string[];
}

interface ScheduleSettings {
  workDaysPerWeek: number;
  maxHoursPerDay: number;
  minBreakBetweenLessons: number;
  autoDistribute: boolean;
  considerTeacherPreferences: boolean;
  balanceWorkload: boolean;
  avoidConsecutiveLessons: boolean;
  maxConsecutiveHours: number;
  lessonDuration: number;
  breakDuration: number;
  schoolStartTime: string;
  lunchBreakStart: string;
  lunchBreakDuration: number;
  workingDaysList: string[];
}

interface TeacherScheduleProps {
  showClassScheduleByDefault?: boolean;
  showClassroomScheduleByDefault?: boolean;
  showAllTeacherSchedulesByDefault?: boolean;
  hidePageHeading?: boolean;
}

type ScheduleView = "availability" | "department" | "teacher";

/** Araç çubuğu — tüm kontroller aynı yükseklikte (h-10); genişlik içeriğe göre */
const SCHED_TOOLBAR_ROW = "flex flex-wrap items-center gap-2 sm:gap-2.5";
const SCHED_CONTROL_H = "box-border h-10 min-h-10 max-h-10 shrink-0";
const SCHED_BTN =
  `${SCHED_CONTROL_H} inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-3.5 text-sm font-medium leading-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed sm:px-4`;
const SCHED_BTN_PRIMARY = `${SCHED_BTN} border-transparent bg-blue-600 text-white shadow-sm hover:bg-blue-700 disabled:opacity-55`;
const SCHED_BTN_INDIGO = `${SCHED_BTN} border-transparent bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 disabled:opacity-55`;
const SCHED_BTN_DANGER = `${SCHED_BTN} border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700 disabled:opacity-55`;
const SCHED_BTN_SECONDARY = `${SCHED_BTN} border-gray-200 bg-white text-gray-800 shadow-sm hover:border-gray-300 hover:bg-gray-50`;
const SCHED_FILTER_TRIGGER = `${SCHED_CONTROL_H} flex min-w-[12rem] max-w-[20rem] cursor-pointer items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium leading-none text-gray-800 shadow-sm transition-colors hover:border-blue-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:px-4`;

const TeacherSchedule = ({
  showClassScheduleByDefault = false,
  showClassroomScheduleByDefault = false,
  showAllTeacherSchedulesByDefault = false,
  hidePageHeading = false,
}: TeacherScheduleProps) => {
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showTeacherDetail, setShowTeacherDetail] = useState(false);
  const [detailTeacherId, setDetailTeacherId] = useState<string | null>(null);
  const [showClassSchedule, setShowClassSchedule] = useState(showClassScheduleByDefault);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [classSchedule, setClassSchedule] = useState<ClassScheduleEntry[]>([]);
  const [showClassroomSchedule, setShowClassroomSchedule] = useState(showClassroomScheduleByDefault);
  const [classroomSchedule, setClassroomSchedule] = useState<ClassroomScheduleEntry[]>([]);
  const [showAllTeacherSchedules, setShowAllTeacherSchedules] = useState(showAllTeacherSchedulesByDefault);
  const [allTeacherSchedules, setAllTeacherSchedules] = useState<Record<string, any[]>>({});
  const [loadingAllSchedules, setLoadingAllSchedules] = useState(false);

  useEffect(() => {
    if (showAllTeacherSchedulesByDefault) {
      loadAllTeacherSchedules();
    }
  }, [showAllTeacherSchedulesByDefault]);

  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [teacherDeptId, setTeacherDeptId] = useState<string | null>(null);
  const [teacherDetailSchedule, setTeacherDetailSchedule] = useState<any[]>([]);
  const [loadingDetailSchedule, setLoadingDetailSchedule] = useState(false);

  const [clearScheduleModalOpen, setClearScheduleModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [distributionResultModalOpen, setDistributionResultModalOpen] = useState(false);
  const [distributionResultMessage, setDistributionResultMessage] = useState("");
  const [loadingClassSchedule, setLoadingClassSchedule] = useState(false);

  const dataFetchedRef = React.useRef(false);

  useEffect(() => {
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;

    const fetchSettings = async () => {
      try {
        const { ApiService } = await import("../../../services/api");

        const [settingsRes, planningSettings] = await Promise.all([
          ApiService.getSettings(),
          ApiService.getPlanningSettings()
        ]);

        const settingsData = settingsRes?.data ?? settingsRes;
        const tDeptId = settingsData?.teacherDepartmentId;

        if (tDeptId) {
          setTeacherDeptId(tDeptId);
        } else {
          console.warn("Öğretmen Departmanı (teacherDepartmentId) ayarı bulunamadı.");
        }

        if (planningSettings) {
          setSettings(prev => ({
            ...prev,
            workDaysPerWeek: planningSettings.workingDays || 5,
            maxHoursPerDay: planningSettings.dailyMaxLessons || 8,
            maxConsecutiveHours: planningSettings.maxConsecutiveLessons || 3,
            lessonDuration: planningSettings.lessonDuration || 40,
            breakDuration: planningSettings.breakBetweenLessons || 10,
            schoolStartTime: planningSettings.schoolStartTime || '08:30',
            lunchBreakStart: planningSettings.lunchBreakStart || '12:00',
            lunchBreakDuration: planningSettings.lunchBreakDuration || 60,
            considerTeacherPreferences: planningSettings.considerTeacherPreferences ?? true,
            balanceWorkload: planningSettings.balanceWorkload ?? true,
            avoidConsecutiveLessons: planningSettings.preventConsecutiveSameLessons ?? false,
            workingDaysList: planningSettings.workingDaysList || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          }));
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const fetchTeachers = async () => {
    if (!teacherDeptId) return;

    setLoadingTeachers(true);
    try {
      const { ApiService } = await import("../../../services/api");
      const response = await ApiService.getPlanningTeachers({
        parentDepartmentId: teacherDeptId,
        limit: 100,
        page: 1,
        sortBy: 'firstName',
        sortOrder: 'ASC'
      });

      const teacherResults = response?.results || [];

      const teacherList: Teacher[] = teacherResults.map((t: any) => ({
        id: t.id,
        name: t.name || `${t.firstName || ''} ${t.lastName || ''}`.trim(),
        branch: t.branch || 'Belirtilmemiş',
        availableHours: t.availableHours || 0,
        assignedLessons: t.totalLessons || 0
      }));

      setTeachers(teacherList);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    } finally {
      setLoadingTeachers(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, [teacherDeptId]);

  const [classDetails, setClassDetails] = useState<Record<string, ClassInfo>>({});

  const classrooms: ClassroomInfo[] = [
    {
      id: "101",
      name: "Fen Laboratuvarı",
      capacity: 30,
      type: "Laboratuvar",
      equipment: ["Mikroskop", "Deney Masası", "Projeksiyon"],
    },
    {
      id: "102",
      name: "Matematik Sınıfı",
      capacity: 35,
      type: "Normal Sınıf",
      equipment: ["Akıllı Tahta", "Projeksiyon"],
    },
    {
      id: "103",
      name: "Türkçe Sınıfı",
      capacity: 32,
      type: "Normal Sınıf",
      equipment: ["Akıllı Tahta"],
    },
    {
      id: "104",
      name: "İngilizce Sınıfı",
      capacity: 28,
      type: "Dil Sınıfı",
      equipment: ["Ses Sistemi", "Akıllı Tahta"],
    },
    {
      id: "105",
      name: "Resim Atölyesi",
      capacity: 25,
      type: "Atölye",
      equipment: ["Şövale", "Lavabo", "Doğal Işık"],
    },
    {
      id: "106",
      name: "Müzik Sınıfı",
      capacity: 30,
      type: "Müzik Sınıfı",
      equipment: ["Piyano", "Ses Sistemi", "Enstrümanlar"],
    },
    {
      id: "107",
      name: "Bilgisayar Laboratuvarı",
      capacity: 24,
      type: "Laboratuvar",
      equipment: ["30 Bilgisayar", "Projeksiyon", "Yazıcı"],
    },
    {
      id: "108",
      name: "Sosyal Bilgiler Sınıfı",
      capacity: 33,
      type: "Normal Sınıf",
      equipment: ["Harita", "Akıllı Tahta"],
    },
  ];

  const [availability, setAvailability] = useState<TeacherAvailability[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  useEffect(() => {
    const loadTeacherAvailability = async () => {
      if (!selectedTeacher) return;

      setLoadingAvailability(true);
      try {
        const { ApiService } = await import("../../../services/api");
        const response = await ApiService.getTeacherAvailability(selectedTeacher);

        const availabilityData: TeacherAvailability[] = [];

        const dayMapReverse: Record<string, string> = {
          'monday': 'Pazartesi',
          'tuesday': 'Salı',
          'wednesday': 'Çarşamba',
          'thursday': 'Perşembe',
          'friday': 'Cuma',
          'saturday': 'Cumartesi',
          'sunday': 'Pazar'
        };

        if (response && Array.isArray(response)) {
          for (const item of response) {
            availabilityData.push({
              teacherId: selectedTeacher,
              day: dayMapReverse[item.dayOfWeek] || item.dayOfWeek,
              timeSlotId: String(item.period),
              isAvailable: item.isAvailable
            });
          }
        }

        setAvailability(prev => {
          const otherTeachersAvail = prev.filter(a => a.teacherId !== selectedTeacher);
          return [...otherTeachersAvail, ...availabilityData];
        });
      } catch (error) {
        console.error('Error loading teacher availability:', error);
      } finally {
        setLoadingAvailability(false);
      }
    };

    loadTeacherAvailability();
  }, [selectedTeacher]);

  const lessonAssignments: LessonAssignment[] = [
    {
      id: "1",
      teacherId: "1",
      classId: "5a",
      subject: "Matematik",
      day: "Pazartesi",
      timeSlotId: "1",
      className: "5-A",
      classGrade: 5,
    },
    {
      id: "2",
      teacherId: "1",
      classId: "5b",
      subject: "Matematik",
      day: "Pazartesi",
      timeSlotId: "3",
      className: "5-B",
      classGrade: 5,
    },
    {
      id: "3",
      teacherId: "1",
      classId: "6a",
      subject: "Matematik",
      day: "Salı",
      timeSlotId: "2",
      className: "6-A",
      classGrade: 6,
    },
    {
      id: "4",
      teacherId: "1",
      classId: "6b",
      subject: "Matematik",
      day: "Çarşamba",
      timeSlotId: "1",
      className: "6-B",
      classGrade: 6,
    },
    {
      id: "5",
      teacherId: "1",
      classId: "7a",
      subject: "Matematik",
      day: "Perşembe",
      timeSlotId: "4",
      className: "7-A",
      classGrade: 7,
    },

    {
      id: "6",
      teacherId: "2",
      classId: "5a",
      subject: "Türkçe",
      day: "Pazartesi",
      timeSlotId: "2",
      className: "5-A",
      classGrade: 5,
    },
    {
      id: "7",
      teacherId: "2",
      classId: "5b",
      subject: "Türkçe",
      day: "Salı",
      timeSlotId: "1",
      className: "5-B",
      classGrade: 5,
    },
    {
      id: "8",
      teacherId: "2",
      classId: "6a",
      subject: "Türkçe",
      day: "Salı",
      timeSlotId: "3",
      className: "6-A",
      classGrade: 6,
    },
    {
      id: "9",
      teacherId: "2",
      classId: "7a",
      subject: "Türkçe",
      day: "Çarşamba",
      timeSlotId: "2",
      className: "7-A",
      classGrade: 7,
    },

    {
      id: "10",
      teacherId: "3",
      classId: "5a",
      subject: "Fen Bilgisi",
      day: "Salı",
      timeSlotId: "4",
      className: "5-A",
      classGrade: 5,
    },
    {
      id: "11",
      teacherId: "3",
      classId: "6a",
      subject: "Fen Bilgisi",
      day: "Çarşamba",
      timeSlotId: "3",
      className: "6-A",
      classGrade: 6,
    },
    {
      id: "12",
      teacherId: "3",
      classId: "6b",
      subject: "Fen Bilgisi",
      day: "Perşembe",
      timeSlotId: "1",
      className: "6-B",
      classGrade: 6,
    },
    {
      id: "13",
      teacherId: "3",
      classId: "7a",
      subject: "Fen Bilgisi",
      day: "Cuma",
      timeSlotId: "2",
      className: "7-A",
      classGrade: 7,
    },

    {
      id: "14",
      teacherId: "4",
      classId: "5a",
      subject: "İngilizce",
      day: "Pazartesi",
      timeSlotId: "4",
      className: "5-A",
      classGrade: 5,
    },
    {
      id: "15",
      teacherId: "4",
      classId: "5b",
      subject: "İngilizce",
      day: "Salı",
      timeSlotId: "2",
      className: "5-B",
      classGrade: 5,
    },
    {
      id: "16",
      teacherId: "4",
      classId: "6a",
      subject: "İngilizce",
      day: "Çarşamba",
      timeSlotId: "4",
      className: "6-A",
      classGrade: 6,
    },
    {
      id: "17",
      teacherId: "4",
      classId: "7a",
      subject: "İngilizce",
      day: "Perşembe",
      timeSlotId: "3",
      className: "7-A",
      classGrade: 7,
    },

    {
      id: "18",
      teacherId: "5",
      classId: "5a",
      subject: "Resim",
      day: "Cuma",
      timeSlotId: "1",
      className: "5-A",
      classGrade: 5,
    },
    {
      id: "19",
      teacherId: "5",
      classId: "5b",
      subject: "Resim",
      day: "Cuma",
      timeSlotId: "3",
      className: "5-B",
      classGrade: 5,
    },
    {
      id: "20",
      teacherId: "5",
      classId: "6a",
      subject: "Resim",
      day: "Perşembe",
      timeSlotId: "2",
      className: "6-A",
      classGrade: 6,
    },
  ];

  const [isDistributing, setIsDistributing] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const [settings, setSettings] = useState<ScheduleSettings>({
    workDaysPerWeek: 5,
    maxHoursPerDay: 8,
    minBreakBetweenLessons: 1,
    autoDistribute: true,
    considerTeacherPreferences: true,
    balanceWorkload: true,
    avoidConsecutiveLessons: false,
    maxConsecutiveHours: 3,
    lessonDuration: 40,
    breakDuration: 10,
    schoolStartTime: '08:30',
    lunchBreakStart: '12:00',
    lunchBreakDuration: 60,
    workingDaysList: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  });

  const allDaysMap: Record<string, string> = {
    monday: 'Pazartesi',
    tuesday: 'Salı',
    wednesday: 'Çarşamba',
    thursday: 'Perşembe',
    friday: 'Cuma',
    saturday: 'Cumartesi',
    sunday: 'Pazar'
  };
  const days = settings.workingDaysList.map(d => allDaysMap[d] || d);

  const dayAbbrevMap: Record<string, string> = {
    'Pazartesi': 'Pzt',
    'Salı': 'Sal',
    'Çarşamba': 'Çar',
    'Perşembe': 'Per',
    'Cuma': 'Cum',
    'Cumartesi': 'Cmt',
    'Pazar': 'Paz'
  };

  const timeSlots: TimeSlot[] = (() => {
    const maxSlots = Number(settings.maxHoursPerDay) || 8;
    const lessonMin = Number(settings.lessonDuration) || 40;
    const breakMin = Number(settings.breakDuration) || 10;
    const startTimeStr = settings.schoolStartTime || '08:30';
    const lunchStartStr = settings.lunchBreakStart || '12:00';
    const lunchDur = Number(settings.lunchBreakDuration) || 60;

    console.log('[TimeSlots] Generating with:', { maxSlots, lessonMin, breakMin, startTimeStr, lunchStartStr, lunchDur });

    const [startH, startM] = startTimeStr.split(':').map(Number);
    const [lunchH, lunchM] = lunchStartStr.split(':').map(Number);
    const lunchStartMinutes = lunchH * 60 + lunchM;
    const lunchEndMinutes = lunchStartMinutes + lunchDur;

    const slots: TimeSlot[] = [];
    let currentMinutes = startH * 60 + startM;

    for (let i = 1; i <= maxSlots; i++) {
      const lessonEnd = currentMinutes + lessonMin;
      if (currentMinutes < lunchEndMinutes && lessonEnd > lunchStartMinutes) {
        currentMinutes = lunchEndMinutes;
      }

      const sH = Math.floor(currentMinutes / 60);
      const sM = currentMinutes % 60;
      const endMinutes = currentMinutes + lessonMin;
      const eH = Math.floor(endMinutes / 60);
      const eM = endMinutes % 60;

      slots.push({
        id: String(i),
        period: `${i}. Saat`,
        startTime: `${String(sH).padStart(2, '0')}:${String(sM).padStart(2, '0')}`,
        endTime: `${String(eH).padStart(2, '0')}:${String(eM).padStart(2, '0')}`,
      });

      currentMinutes = endMinutes + breakMin;
    }

    return slots;
  })();

  const handleClearScheduleClick = () => {
    setClearScheduleModalOpen(true);
  };

  const [clearSuccessful, setClearSuccessful] = useState(false);

  const confirmClearSchedule = async () => {
    setClearScheduleModalOpen(false);
    setIsClearing(true);
    try {
      const { ApiService } = await import("../../../services/api");

      await ApiService.clearSchedule(undefined, false);
      setSuccessMessage("Tüm ders programı başarıyla sıfırlandı.");
      setClearSuccessful(true);
      setSuccessModalOpen(true);
    } catch (error) {
      console.error("Schedule clear error:", error);
      setErrorMessage("Program sıfırlanırken bir hata oluştu.");
      setErrorModalOpen(true);
    } finally {
      setIsClearing(false);
    }
  };

  const handleSuccessConfirm = () => {
    setSuccessModalOpen(false);
    if (clearSuccessful) {
      setClearSuccessful(false);

      fetchTeachers();
      setClassSchedule([]);
      setSelectedTeacher(null);
    }
  };

  const filteredTeachers = teachers.filter(
    (teacher) =>
      teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) || teacher.branch.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const toggleAvailability = (day: string, timeSlotId: string) => {
    if (!selectedTeacher) return;

    setAvailability((prev) => {
      const existingItem = prev.find((item) => item.teacherId === selectedTeacher && item.day === day && item.timeSlotId === timeSlotId);

      if (existingItem) {
        return prev.map((item) =>
          item.teacherId === selectedTeacher && item.day === day && item.timeSlotId === timeSlotId
            ? { ...item, isAvailable: !item.isAvailable }
            : item,
        );
      } else {
        return [
          ...prev,
          {
            teacherId: selectedTeacher,
            day,
            timeSlotId,
            isAvailable: true,
          },
        ];
      }
    });
  };

  const toggleDayAvailability = (day: string, setAvailable: boolean) => {
    if (!selectedTeacher) return;

    setAvailability((prev) => {
      let updated = [...prev];

      for (const slot of timeSlots) {
        const existingIndex = updated.findIndex(
          (item) => item.teacherId === selectedTeacher && item.day === day && item.timeSlotId === slot.id
        );

        if (existingIndex >= 0) {
          updated[existingIndex] = { ...updated[existingIndex], isAvailable: setAvailable };
        } else {
          updated.push({
            teacherId: selectedTeacher,
            day,
            timeSlotId: slot.id,
            isAvailable: setAvailable,
          });
        };
      }

      return updated;
    });
  };

  const getAvailability = (teacherId: string, day: string, timeSlotId: string) => {
    const item = availability.find((a) => a.teacherId === teacherId && a.day === day && a.timeSlotId === timeSlotId);
    return item?.isAvailable ?? false;
  };

  const getTeacherAvailableHours = (teacherId: string) => {
    if (!teacherId) return 0;

    const totalPossibleSlots = days.length * timeSlots.length;
    const unavailableCount = availability.filter((a) => a.teacherId === teacherId && !a.isAvailable).length;

    return totalPossibleSlots - unavailableCount;
  };

  const getTeacherLessons = (teacherId: string) => {
    return lessonAssignments.filter((lesson) => lesson.teacherId === teacherId);
  };

  const openTeacherDetail = async (teacherId: string) => {
    setDetailTeacherId(teacherId);
    setShowTeacherDetail(true);
    setLoadingDetailSchedule(true);
    setTeacherDetailSchedule([]);

    try {
      const { ApiService } = await import("../../../services/api");
      const response = await ApiService.getTeacherScheduleEntries(teacherId);
      const scheduleData = response?.data ?? response ?? [];
      setTeacherDetailSchedule(Array.isArray(scheduleData) ? scheduleData : []);
    } catch (error) {
      console.error('Error loading teacher schedule:', error);
      setTeacherDetailSchedule([]);
    } finally {
      setLoadingDetailSchedule(false);
    }
  };

  const closeTeacherDetail = () => {
    setShowTeacherDetail(false);
    setDetailTeacherId(null);
    setTeacherDetailSchedule([]);
  };

  const generateClassScheduleFromLessons = async () => {
    setLoadingClassSchedule(true);
    try {
      const { ApiService } = await import("../../../services/api");

      const response = await ApiService.getAllClassSchedulesAggregated();
      const classSchedules = response?.data ?? response ?? [];

      if (!Array.isArray(classSchedules)) {
        console.error("Beklenmeyen veri formatı:", classSchedules);
        return;
      }

      const dayMap: Record<string, string> = {
        monday: "Pazartesi",
        tuesday: "Salı",
        wednesday: "Çarşamba",
        thursday: "Perşembe",
        friday: "Cuma",
        saturday: "Cumartesi",
        sunday: "Pazar"
      };

      const allEntries: ClassScheduleEntry[] = [];
      const newClassDetails: Record<string, ClassInfo> = {};

      classSchedules.forEach((cls: any) => {
        let gradeLevel = 0;
        const gradeMatch = cls.gradeLabel?.match(/(\d+)/);
        if (gradeMatch) {
          gradeLevel = parseInt(gradeMatch[0]);
        }

        newClassDetails[cls.classId] = {
          id: cls.classId,
          name: cls.className,
          grade: gradeLevel,
          gradeLabel: cls.gradeLabel,
          studentCount: cls.studentCount
        } as any;

        if (Array.isArray(cls.schedule)) {
          cls.schedule.forEach((s: any) => {
            allEntries.push({
              id: s.id,
              classId: cls.classId,
              className: cls.className,
              day: dayMap[s.dayOfWeek.toLowerCase()] || s.dayOfWeek,
              timeSlotId: String(s.period),
              subject: s.lessonName,
              teacherId: s.teacherId || '',
              teacherName: s.teacherName || 'Öğretmen'
            });
          });
        }
      });

      setClassSchedule(allEntries);
      setClassDetails(prev => ({ ...prev, ...newClassDetails }));

      if (!showClassScheduleByDefault) {
        setShowClassSchedule(true);
      }
    } catch (error) {
      console.error("Sınıf programları yüklenirken hata:", error);
      setErrorMessage("Sınıf programları yüklenemedi.");
      setErrorModalOpen(true);
    } finally {
      setLoadingClassSchedule(false);
    }
  };

  const generateClassroomScheduleFromLessons = () => {
    const schedule: ClassroomScheduleEntry[] = [];

    const classroomAssignments = [
      { subject: "Fen Bilgisi", classroomId: "101" },

      { subject: "Matematik", classroomId: "102" },

      { subject: "Türkçe", classroomId: "103" },

      { subject: "İngilizce", classroomId: "104" },

      { subject: "Resim", classroomId: "105" },

      { subject: "Müzik", classroomId: "106" },

      { subject: "Sosyal Bilgiler", classroomId: "108" },
    ];

    lessonAssignments.forEach((lesson) => {
      const teacher = teachers.find((t) => t.id === lesson.teacherId);
      const classroomAssignment = classroomAssignments.find((ca) => ca.subject === lesson.subject);
      const classroom = classrooms.find((cr) => cr.id === classroomAssignment?.classroomId);

      if (teacher && classroom) {
        schedule.push({
          id: `${classroom.id}-${lesson.day}-${lesson.timeSlotId}`,
          classroomId: classroom.id,
          classroomName: classroom.name,
          day: lesson.day,
          timeSlotId: lesson.timeSlotId,
          subject: lesson.subject,
          teacherId: lesson.teacherId,
          teacherName: teacher.name,
          classId: lesson.classId,
          className: lesson.className,
        });
      }
    });

    setClassroomSchedule(schedule);
    if (!showClassroomScheduleByDefault) {
      setShowClassroomSchedule(true);
    }
  };

  useEffect(() => {
    if (showClassSchedule && classSchedule.length === 0) {
      generateClassScheduleFromLessons();
    }
  }, [showClassSchedule]);

  useEffect(() => {
    if (showClassroomScheduleByDefault) {
      generateClassroomScheduleFromLessons();
    }
  }, [showClassroomScheduleByDefault]);

  const [distributionResult, setDistributionResult] = useState<any>(null);

  const autoDistributeSchedule = async () => {
    setIsDistributing(true);
    try {
      const { ApiService } = await import("../../../services/api");
      const result = await ApiService.generateSchedule({
        clearExisting: true,
      });

      setDistributionResult(result);
      setDistributionResultMessage("Ders programları başarıyla oluşturuldu. Programları görüntülemek için aşağıdaki Programları Görüntüle butonuna tıklayın.");
      setDistributionResultModalOpen(true);

      fetchTeachers();
    } catch (error: any) {
      console.error("Otomatik dağıtım hatası:", error);
      setErrorMessage("Otomatik dağıtım sırasında bir hata oluştu: " + (error.message || "Bilinmeyen hata"));
      setErrorModalOpen(true);
    } finally {
      setIsDistributing(false);
    }
  };

  const handleDistributionResultConfirm = () => {
    setDistributionResultModalOpen(false);
    generateClassScheduleFromLessons();
  };

  const [isSaving, setIsSaving] = useState(false);

  const saveSchedule = async () => {
    if (!selectedTeacher) {
      setErrorMessage("Lütfen önce bir öğretmen seçin.");
      setErrorModalOpen(true);
      return;
    }

    setIsSaving(true);
    try {
      const { ApiService } = await import("../../../services/api");

      const teacherAvailability = availability.filter(a => a.teacherId === selectedTeacher);

      const weekly: Record<string, Array<{ period: number; isAvailable: boolean; preferenceLevel: number }>> = {};

      for (const a of teacherAvailability) {
        const dayMap: Record<string, string> = {
          'Pazartesi': 'monday',
          'Salı': 'tuesday',
          'Çarşamba': 'wednesday',
          'Perşembe': 'thursday',
          'Cuma': 'friday',
          'Cumartesi': 'saturday',
          'Pazar': 'sunday'
        };

        const dayOfWeek = dayMap[a.day] || a.day.toLowerCase();

        if (!weekly[dayOfWeek]) {
          weekly[dayOfWeek] = [];
        }

        weekly[dayOfWeek].push({
          period: parseInt(a.timeSlotId),
          isAvailable: a.isAvailable,
          preferenceLevel: 2
        });
      }

      await ApiService.setTeacherAvailability(selectedTeacher, { weekly });

      setSuccessMessage("Müsaitlik durumları başarıyla kaydedildi!");
      setSuccessModalOpen(true);

      const currentAvailableHours = getTeacherAvailableHours(selectedTeacher);
      setTeachers(prev => prev.map(t =>
        t.id === selectedTeacher ? { ...t, availableHours: currentAvailableHours } : t
      ));
    } catch (error: any) {
      console.error("Kaydetme hatası:", error);
      setErrorMessage("Kaydetme sırasında bir hata oluştu: " + (error.message || "Bilinmeyen hata"));
      setErrorModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const renderScheduleGrid = () => {
    if (!selectedTeacher) {
      return (
        <div className="text-center py-12 text-gray-500">
          <Calendar size={64} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Öğretmen Seçin</h3>
          <p>Ders programını düzenlemek için soldaki listeden bir öğretmen seçin</p>
        </div>
      );
    }

    const teacher = teachers.find((t) => t.id === selectedTeacher);

    return (
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{teacher?.name} - Ders Programı</h3>
            <p className="text-sm text-gray-600">
              Branş: {teacher?.branch} | Müsait Saat: {getTeacherAvailableHours(selectedTeacher)}
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <button
              onClick={saveSchedule}
              disabled={isSaving}
              className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-lg transition-colors w-full md:w-auto ${isSaving
                ? 'bg-green-400 text-white cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
                }`}>
              <Save size={16} className={isSaving ? 'animate-pulse' : ''} />
              <span>{isSaving ? 'Kaydediliyor...' : 'Kaydet'}</span>
            </button>
            <button
              onClick={autoDistributeSchedule}
              disabled={isDistributing}
              className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-lg transition-colors w-full md:w-auto ${isDistributing
                ? 'bg-blue-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}>
              <RefreshCw size={16} className={isDistributing ? 'animate-spin' : ''} />
              <span>{isDistributing ? 'Dağıtılıyor...' : 'Bu Öğretmeni Dağıt'}</span>
            </button>
          </div>
        </div>

        <WeeklyAvailabilityGrid
          days={days}
          timeSlots={timeSlots}
          availability={availability}
          selectedEntityId={selectedTeacher}
          getEntityId={(item) => item.teacherId}
          getIsAvailable={(item) => item.isAvailable}
          getDay={(item) => item.day}
          getTimeSlotId={(item) => item.timeSlotId}
          onToggle={toggleAvailability}
          onToggleDay={toggleDayAvailability}
          showLegend={true}
        />
      </div>
    );
  };

  const renderClassroomSchedule = () => {
    if (!showClassroomSchedule) return null;

    const scheduleByClassroom = classroomSchedule.reduce(
      (acc, entry) => {
        if (!acc[entry.classroomId]) {
          acc[entry.classroomId] = {
            classroomInfo: classrooms.find((c) => c.id === entry.classroomId) || {
              id: entry.classroomId,
              name: entry.classroomName,
              capacity: 30,
              type: "Normal Sınıf",
              equipment: [],
            },
            schedule: {},
          };
        }

        if (!acc[entry.classroomId].schedule[entry.day]) {
          acc[entry.classroomId].schedule[entry.day] = {};
        }

        acc[entry.classroomId].schedule[entry.day][entry.timeSlotId] = entry;
        return acc;
      },
      {} as Record<string, { classroomInfo: ClassroomInfo; schedule: Record<string, Record<string, ClassroomScheduleEntry>> }>,
    );

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className={`mb-6 flex items-center ${hidePageHeading ? "justify-end" : "justify-between"} gap-3`}>
            {!hidePageHeading && <h2 className="text-2xl font-bold text-gray-900">Derslik Programları</h2>}
            <div className={SCHED_TOOLBAR_ROW}>
              {!showClassroomScheduleByDefault && (
                <button
                  type="button"
                  onClick={() => setShowClassroomSchedule(false)}
                  className={SCHED_BTN_SECONDARY}>
                  <GraduationCap size={16} className="shrink-0" />
                  <span>Öğretmen programları</span>
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {Object.entries(scheduleByClassroom).map(([classroomId, { classroomInfo, schedule }]) => (
              <div key={classroomId} className="border border-gray-200 rounded-lg">
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold">{classroomInfo.name}</h3>
                      <p className="text-purple-100 text-sm">
                        {classroomInfo.type} • Kapasite: {classroomInfo.capacity}
                      </p>
                    </div>
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Building size={20} />
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <div className="overflow-x-auto">
                    <div className="min-w-[800px]">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="p-2 text-left font-medium text-gray-700 border-r border-gray-200">Saat</th>
                            {days.map((day) => (
                              <th key={day} className="p-2 text-center font-medium text-gray-700 border-r border-gray-200 last:border-r-0">
                                {dayAbbrevMap[day] || day.slice(0, 3)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {timeSlots.map((slot) => (
                            <tr key={slot.id} className="border-t border-gray-200">
                              <td className="p-2 bg-gray-50 border-r border-gray-200 font-medium">
                                <div className="text-xs font-semibold text-gray-900">{slot.period}</div>
                                <div className="text-xs text-gray-500">{slot.startTime}-{slot.endTime}</div>
                              </td>
                              {days.map((day) => {
                                const lesson = schedule[day]?.[slot.id];
                                return (
                                  <td key={`${day}-${slot.id}`} className="p-1 border-r border-gray-200 last:border-r-0">
                                    {lesson ? (
                                      <div className="bg-gradient-to-br from-orange-100 to-orange-200 border border-orange-300 rounded p-1.5 text-center">
                                        <div className="font-semibold text-orange-800 text-xs leading-tight">{lesson.subject}</div>
                                        <div className="text-orange-600 text-xs mt-0.5">{lesson.className}</div>
                                        <div className="text-orange-500 text-xs">
                                          {(() => {
                                            const parts = lesson.teacherName.trim().split(/\s+/);
                                            if (parts.length === 1) return parts[0];
                                            const lastName = parts.pop();
                                            return `${parts.join(" ")} ${lastName?.[0]}.`;
                                          })()}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="h-12 bg-gray-50 rounded border-2 border-dashed border-gray-200"></div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  interface ScheduledTeacher {
    id: string;
    name: string;
    branch: string;
    assignedLessons: number;
    schedule: any[];
  }

  const [scheduledTeachers, setScheduledTeachers] = useState<ScheduledTeacher[]>([]);
  const [allBranches, setAllBranches] = useState<string[]>([]);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState("all");
  const [isBranchFilterOpen, setIsBranchFilterOpen] = useState(false);
  const [branchSearchTerm, setBranchSearchTerm] = useState("");

  const loadAllTeacherSchedules = async (branchFilter?: string) => {
    setShowAllTeacherSchedules(true);
    setLoadingAllSchedules(true);

    try {
      const { ApiService } = await import("../../../services/api");

      const filterToSend = branchFilter === 'all' ? undefined : branchFilter;
      const response = await ApiService.getScheduledTeachers(filterToSend);
      const data = response || [];

      if (!branchFilter || branchFilter === 'all') {
        const branches = Array.from(new Set(data.map((t: any) => {
          const parts = (t.branch || "Diğer").split(',');

          return parts[0].trim();
        }))).sort() as string[];

        setAllBranches(branches);
      };

      const dayMap: Record<string, string> = {
        monday: "Pazartesi", tuesday: "Salı", wednesday: "Çarşamba",
        thursday: "Perşembe", friday: "Cuma", saturday: "Cumartesi", sunday: "Pazar"
      };

      const processedTeachers = data.map((t: any) => ({
        ...t,
        schedule: (t.schedule || []).map((entry: any) => ({
          ...entry,
          day: dayMap[entry.dayOfWeek?.toLowerCase()] || entry.dayOfWeek
        }))
      }));

      setScheduledTeachers(processedTeachers);
    } catch (error) {
      console.error("Ders programı yüklenirken hata:", error);

      setErrorMessage("Ders programları yüklenemedi.");
      setErrorModalOpen(true);
    } finally {
      setLoadingAllSchedules(false);
    }
  };

  useEffect(() => {
    if (showAllTeacherSchedules) loadAllTeacherSchedules(selectedBranchFilter);
  }, [selectedBranchFilter]);

  const exportAllTeacherSchedulesToExcel = async () => {
    setIsExporting(true);
    try {
      const { ApiService } = await import("../../../services/api");
      const blob = await ApiService.exportTeacherSchedules(selectedBranchFilter !== "all" ? selectedBranchFilter : undefined);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `Tum_Ogretmen_Programlari_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage("Öğretmen programları Excel olarak başarıyla oluşturuldu.");
      setSuccessModalOpen(true);
    } catch (error: any) {
      console.error("Dışa aktarma hatası:", error);

      setErrorMessage("Excel dosyası oluşturulurken bir hata oluştu: " + (error.message || "Bilinmeyen hata"));
      setErrorModalOpen(true);
    } finally {
      setIsExporting(false);
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const exportClassSchedulesToCSV = async () => {
    setIsExporting(true);
    try {
      const { ApiService } = await import("../../../services/api");
      const blob = await ApiService.exportDepartmentSchedules();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `Departman_Programlari_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage("Departman programları Excel olarak başarıyla oluşturuldu.");
      setSuccessModalOpen(true);
    } catch (error: any) {
      console.error("Dışa aktarma hatası:", error);

      setErrorMessage("Excel dosyası oluşturulurken bir hata oluştu: " + (error.message || "Bilinmeyen hata"));
      setErrorModalOpen(true);
    } finally {
      setIsExporting(false);
    }
  };

  const getCurrentScheduleView = (): ScheduleView => {
    if (showClassSchedule) return "department";
    if (showAllTeacherSchedules) return "teacher";
    return "availability";
  };

  const openAvailabilityView = () => {
    setShowClassSchedule(false);
    setShowClassroomSchedule(false);
    setShowAllTeacherSchedules(false);
  };

  const openDepartmentScheduleView = () => {
    setShowAllTeacherSchedules(false);
    setShowClassroomSchedule(false);

    if (classSchedule.length === 0) {
      generateClassScheduleFromLessons();
      return;
    }

    setShowClassSchedule(true);
  };

  const openTeacherScheduleView = () => {
    setShowClassSchedule(false);
    setShowClassroomSchedule(false);
    void loadAllTeacherSchedules(selectedBranchFilter);
  };

  const renderScheduleViewSwitcher = (currentView: ScheduleView, trailing?: React.ReactNode) => {
    const viewButtons = [
      { id: "availability" as const, label: "Öğretmen Müsaitlikleri", icon: Clock, onClick: openAvailabilityView },
      { id: "department" as const, label: "Departman Programları", icon: Building, onClick: openDepartmentScheduleView },
      { id: "teacher" as const, label: "Öğretmen Programları", icon: Calendar, onClick: openTeacherScheduleView },
    ];

    return (
      <div className="border-b border-gray-200 bg-white px-4 sm:px-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
          <nav
            className="flex min-w-0 flex-1 flex-wrap items-center gap-x-6 gap-y-1"
            aria-label="Program görünümü"
          >
            {viewButtons.map(({ id, label, icon: Icon, onClick }) => {
              const isActive = currentView === id;

              return (
                <button
                  key={id}
                  type="button"
                  onClick={onClick}
                  className={`relative pb-3 pt-3 text-sm font-medium transition-colors ${
                    isActive
                      ? "text-blue-600 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-blue-500"
                      : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon size={16} className="shrink-0 opacity-80" aria-hidden />
                    {label}
                  </span>
                </button>
              );
            })}
          </nav>
          {trailing ? (
            <div className={`${SCHED_TOOLBAR_ROW} w-full shrink-0 justify-end md:w-auto`}>{trailing}</div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderAllTeacherSchedules = () => {
    if (!showAllTeacherSchedules) return null;

    const dayMapping: Record<string, string> = {
      'monday': 'Pazartesi',
      'tuesday': 'Salı',
      'wednesday': 'Çarşamba',
      'thursday': 'Perşembe',
      'friday': 'Cuma',
      'saturday': 'Cumartesi',
      'sunday': 'Pazar'
    };

    return (
      <div className="space-y-6">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {!hidePageHeading && (
            <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-4 sm:px-6">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Tüm öğretmen programları</h2>
                <p className="mt-0.5 text-sm text-gray-500">Öğretmen ders programları listesi</p>
              </div>
            </div>
          )}

          {renderScheduleViewSwitcher(
            "teacher",
            <>
              <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsBranchFilterOpen(!isBranchFilterOpen)}
                    className={SCHED_FILTER_TRIGGER}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2 truncate">
                      <Filter size={16} className="shrink-0 text-blue-600" />
                      <span className="truncate">
                        {selectedBranchFilter === "all" ? "Tüm branşlar" : selectedBranchFilter}
                      </span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`shrink-0 text-gray-400 transition-transform duration-200 ${isBranchFilterOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isBranchFilterOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none"
                        onClick={() => setIsBranchFilterOpen(false)}
                      />
                      <div className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] w-full animate-in overflow-hidden rounded-t-2xl border-t border-gray-100 bg-white shadow-2xl duration-200 fade-in slide-in-from-bottom-5 md:absolute md:inset-x-auto md:bottom-auto md:left-auto md:right-0 md:top-full md:mt-2 md:max-h-[400px] md:w-72 md:rounded-xl md:border md:shadow-xl md:slide-in-from-top-2">
                        <div className="border-b border-gray-50 bg-gray-50/50 p-4 md:p-3">
                          <div className="mb-3 flex justify-center md:hidden">
                            <div className="h-1.5 w-12 rounded-full bg-gray-200" />
                          </div>

                          <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Branş ara..."
                              value={branchSearchTerm}
                              onChange={(e) => setBranchSearchTerm(e.target.value)}
                              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              autoFocus
                            />
                          </div>
                        </div>

                        <div className="max-h-[50vh] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 md:max-h-[300px]">
                          <div className="space-y-1 p-2">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedBranchFilter("all");
                                setIsBranchFilterOpen(false);
                              }}
                              className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors ${
                                selectedBranchFilter === "all"
                                  ? "bg-blue-50 font-medium text-blue-700"
                                  : "text-gray-700 hover:bg-gray-50"
                              } ${!branchSearchTerm || "Tüm Branşlar".toLowerCase().includes(branchSearchTerm.toLowerCase()) ? "flex" : "hidden"}`}
                            >
                              <span>Tüm Branşlar</span>
                              {selectedBranchFilter === "all" && <Check size={16} className="text-blue-600" />}
                            </button>

                            {allBranches
                              .filter((branch) => !branchSearchTerm || branch.toLowerCase().includes(branchSearchTerm.toLowerCase()))
                              .map((branch) => (
                                <button
                                  key={branch}
                                  type="button"
                                  onClick={() => {
                                    setSelectedBranchFilter(branch);
                                    setIsBranchFilterOpen(false);
                                  }}
                                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors ${
                                    selectedBranchFilter === branch
                                      ? "bg-blue-50 font-medium text-blue-700"
                                      : "text-gray-700 hover:bg-gray-50"
                                  }`}
                                >
                                  <span className="truncate">{branch}</span>
                                  {selectedBranchFilter === branch && <Check size={16} className="text-blue-600" />}
                                </button>
                              ))}

                            {allBranches.filter(
                              (b) => !branchSearchTerm || b.toLowerCase().includes(branchSearchTerm.toLowerCase()),
                            ).length === 0 &&
                              (!branchSearchTerm ||
                                "Tüm Branşlar".toLowerCase().includes(branchSearchTerm.toLowerCase()) === false) && (
                                <div className="px-3 py-8 text-center text-sm text-gray-400">Sonuç bulunamadı</div>
                              )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <button
                  type="button"
                  onClick={exportAllTeacherSchedulesToExcel}
                  disabled={isExporting}
                  className={SCHED_BTN_PRIMARY}
                  title="Excel olarak dışa aktar"
                >
                  {isExporting ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <Download size={16} className="shrink-0" />
                  )}
                  <span>{isExporting ? "İndiriliyor..." : "İndir"}</span>
                </button>

                {!showAllTeacherSchedulesByDefault && (
                  <button
                    type="button"
                    onClick={() => setShowAllTeacherSchedules(false)}
                    className={SCHED_BTN_SECONDARY}
                  >
                    <X size={16} className="shrink-0" />
                    <span>Geri dön</span>
                  </button>
                )}
            </>
          )}

          <div className="p-4 sm:p-6">
            {loadingAllSchedules ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                      <div className="h-20 animate-pulse bg-gray-100 p-5">
                        <div className="mb-2 h-6 w-1/3 rounded bg-gray-200" />
                        <div className="h-4 w-1/4 rounded bg-gray-200" />
                      </div>
                      <div className="space-y-4 p-5">
                        <div className="h-4 w-full animate-pulse rounded bg-gray-100" />
                        <div className="h-20 animate-pulse rounded bg-gray-50" />
                        <div className="h-20 animate-pulse rounded bg-gray-50" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {scheduledTeachers.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 py-12 text-center text-gray-500">
                    <GraduationCap size={48} className="mx-auto mb-4 text-gray-300" />
                    <p className="text-sm">Görüntülenecek ders programı bulunamadı.</p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  {scheduledTeachers.map((teacher) => {
                    const schedule = teacher.schedule || [];
                    const byDay: Record<string, any[]> = {};
                    schedule.forEach((entry: any) => {
                      const dayName = dayMapping[entry.dayOfWeek] || entry.dayOfWeek;
                      if (!byDay[dayName]) byDay[dayName] = [];
                      byDay[dayName].push(entry);
                    });

                    Object.keys(byDay).forEach((day) => {
                      byDay[day].sort((a: any, b: any) => a.period - b.period);
                    });

                    return (
                      <div
                        key={teacher.id}
                        className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
                      >
                        <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-4 sm:px-5">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="shrink-0 rounded-lg bg-blue-100 p-2.5">
                                <GraduationCap size={22} className="text-blue-600" aria-hidden />
                              </div>
                              <div className="min-w-0">
                                <h4 className="truncate text-base font-bold text-gray-900 sm:text-lg">{teacher.name}</h4>
                                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-600 sm:text-sm">
                                  <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                                    {teacher.branch}
                                  </span>
                                  <span className="text-gray-400">·</span>
                                  <span>{teacher.assignedLessons} ders</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 sm:p-5">
                          <div className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 overflow-x-auto pb-2">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="border-r border-gray-200 p-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                                    Saat
                                  </th>
                                  {days.map((day) => (
                                    <th
                                      key={day}
                                      className="border-r border-gray-200 p-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500 last:border-r-0"
                                    >
                                      {dayAbbrevMap[day] || day.slice(0, 3)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {timeSlots.map((slot) => (
                                  <tr key={slot.id} className="border-t border-gray-100 transition-colors hover:bg-gray-50/40">
                                    <td className="border-r border-gray-200 bg-gray-50/80 p-2.5 font-semibold">
                                      <div className="text-[11px] text-gray-900">{slot.period}</div>
                                      <div className="mt-0.5 text-[10px] font-medium text-gray-400">
                                        {slot.startTime}-{slot.endTime}
                                      </div>
                                    </td>
                                    {days.map((day) => {
                                      const lesson = byDay[day]?.find((e: any) => e.period === parseInt(slot.id));
                                      return (
                                        <td key={`${day}-${slot.id}`} className="border-r border-gray-100 p-1 align-middle last:border-r-0">
                                          {lesson ? (
                                            <div className="rounded-lg border border-blue-100 bg-blue-50/90 p-2 text-center shadow-sm">
                                              <div className="mb-0.5 text-[11px] font-semibold leading-tight text-gray-900">
                                                {lesson.lessonName ||
                                                  lesson.lesson?.name ||
                                                  lesson.lesson?.lessonName ||
                                                  "Ders"}
                                              </div>
                                              <div className="text-[10px] font-medium text-gray-600">
                                                {lesson.className || lesson.class?.name || "Sınıf"}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="h-10 rounded-lg border border-dashed border-gray-200 bg-gray-50/50 transition-colors hover:border-gray-300" />
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderClassSchedule = () => {
    if (!showClassSchedule) return null;

    const scheduleByClass = classSchedule.reduce(
      (acc, entry) => {
        if (!acc[entry.classId]) {
          acc[entry.classId] = {
            classInfo: classDetails[entry.classId] || {
              id: entry.classId,
              name: entry.className,
              grade: 0,
              studentCount: 0,
            },
            schedule: {},
          };
        }

        if (!acc[entry.classId].schedule[entry.day]) {
          acc[entry.classId].schedule[entry.day] = {};
        }

        acc[entry.classId].schedule[entry.day][entry.timeSlotId] = entry;
        return acc;
      },
      {} as Record<string, { classInfo: ClassInfo; schedule: Record<string, Record<string, ClassScheduleEntry>> }>,
    );

    const groupedByGrade = Object.values(scheduleByClass).reduce(
      (acc, item) => {
        const gradeLabel = (item.classInfo as any).gradeLabel || "Genel";
        if (!acc[gradeLabel]) acc[gradeLabel] = [];
        acc[gradeLabel].push(item);
        return acc;
      },
      {} as Record<string, typeof scheduleByClass[string][]>
    );

    const sortedCategories = Object.keys(groupedByGrade).sort();

    return (
      <div className="space-y-6">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col">
            {!hidePageHeading && (
              <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-4 sm:px-6">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Departman Programları</h2>
                  <p className="mt-0.5 text-sm text-gray-500">Kategori bazlı sınıf ders programları listesi</p>
                </div>
              </div>
            )}
            {renderScheduleViewSwitcher(
              "department",
              <>
                <button
                  type="button"
                  onClick={exportClassSchedulesToCSV}
                  disabled={isExporting}
                  className={SCHED_BTN_PRIMARY}
                  title="Excel olarak dışa aktar">
                  {isExporting ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <Download size={16} className="shrink-0" />}
                  <span>{isExporting ? 'Aktarılıyor...' : 'Dışa aktar'}</span>
                </button>
                {!showClassScheduleByDefault && (
                  <button
                    type="button"
                    onClick={() => setShowClassSchedule(false)}
                    className={SCHED_BTN_SECONDARY}>
                    <GraduationCap size={16} className="shrink-0" />
                    <span>Öğretmen programları</span>
                  </button>
                )}
              </>,
            )}
          </div>

          <div className="p-4 sm:p-6">
          {loadingClassSchedule ? (
            <div className="space-y-8 animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              {[1, 2].map((grade) => (
                <div key={grade} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 p-4 border-b border-gray-200">
                    <div className="h-6 bg-gray-200 rounded w-1/6"></div>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2].map((cls) => (
                      <div key={cls} className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                        <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
                        <div className="space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-full"></div>
                          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-12">
              {sortedCategories.map((gradeLabel) => (
                <div key={gradeLabel} className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="h-px flex-1 bg-gray-200"></div>
                    <h3 className="text-xl font-bold text-blue-600 bg-blue-50 px-6 py-2 rounded-full border border-blue-100">
                      {gradeLabel}
                    </h3>
                    <div className="h-px flex-1 bg-gray-200"></div>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    {groupedByGrade[gradeLabel].map(({ classInfo, schedule }) => (
                      <div key={classInfo.id} className="border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow bg-white">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-5">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-xl font-bold tracking-tight">{classInfo.name} Sınıfı</h4>
                              <div className="flex items-center space-x-2 text-blue-100 mt-1">
                                <span className="bg-white/20 px-2.5 py-0.5 rounded-full text-xs font-semibold backdrop-blur-sm border border-white/10 uppercase">
                                  {(classInfo as any).gradeLabel || (classInfo.grade > 0 ? `${classInfo.grade}. Sınıf` : 'Sınıf')}
                                </span>
                                <span className="text-blue-200">•</span>
                                <span className="text-sm">{classInfo.studentCount} Öğrenci</span>
                              </div>
                            </div>
                            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md border border-white/20">
                              <Users size={24} />
                            </div>
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent pb-2">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-gray-50/50">
                                  <th className="p-2.5 text-left font-bold text-gray-500 border-r border-gray-100 uppercase tracking-wider">Saat</th>
                                  {days.map((day) => (
                                    <th key={day} className="p-2.5 text-center font-bold text-gray-500 border-r border-gray-100 last:border-r-0 uppercase tracking-wider">
                                      {dayAbbrevMap[day] || day.slice(0, 3)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {timeSlots.map((slot) => (
                                  <tr key={slot.id} className="border-t border-gray-100 hover:bg-gray-50/30 transition-colors">
                                    <td className="p-2.5 bg-gray-50/50 border-r border-gray-100 font-bold">
                                      <div className="text-gray-900 text-[11px]">{slot.period}</div>
                                      <div className="text-[10px] text-gray-400 font-medium mt-0.5">{slot.startTime}-{slot.endTime}</div>
                                    </td>
                                    {days.map((day) => {
                                      const lesson = schedule[day]?.[slot.id];
                                      return (
                                        <td key={`${day}-${slot.id}`} className="p-1 border-r border-gray-100 last:border-r-0 align-middle">
                                          {lesson ? (
                                            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100/80 rounded-lg p-2 text-center shadow-sm">
                                              <div className="font-bold text-emerald-900 text-[11px] leading-tight mb-1">{lesson.subject}</div>
                                              <div className="text-emerald-600 text-[10px] font-medium flex items-center justify-center space-x-1">
                                                <span className="opacity-70">
                                                  {lesson.teacherName.split(" ").length >= 2
                                                    ? `${lesson.teacherName.split(" ")[0]} ${lesson.teacherName.split(" ").slice(1).map((n) => n[0]).join("")}.`
                                                    : lesson.teacherName}
                                                </span>
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="h-10 bg-gray-50/30 rounded-lg border border-dashed border-gray-100 transition-colors hover:border-gray-200"></div>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>
    );
  };

  const renderTeacherDetailModal = () => {
    if (!detailTeacherId) return null;

    const teacher = teachers.find((t) => t.id === detailTeacherId);
    if (!teacher) return null;

    const dayMapping: Record<string, string> = {
      'monday': 'Pazartesi',
      'tuesday': 'Salı',
      'wednesday': 'Çarşamba',
      'thursday': 'Perşembe',
      'friday': 'Cuma',
      'saturday': 'Cumartesi',
      'sunday': 'Pazar'
    };

    const lessonsByDay: Record<string, any[]> = {};
    teacherDetailSchedule.forEach((entry: any) => {
      const dayName = dayMapping[entry.dayOfWeek] || entry.dayOfWeek;
      if (!lessonsByDay[dayName]) {
        lessonsByDay[dayName] = [];
      }
      lessonsByDay[dayName].push(entry);
    });

    Object.keys(lessonsByDay).forEach((day) => {
      lessonsByDay[day].sort((a, b) => a.period - b.period);
    });

    const uniqueClasses = new Set(teacherDetailSchedule.map((e: any) => e.classId || e.class?.id));
    const totalLessons = teacherDetailSchedule.length;

    return (
      <Modal
        isOpen={showTeacherDetail}
        onClose={closeTeacherDetail}
        title={`${teacher.name} - ${teacher.branch} Öğretmeni`}
        size="lg"
      >
        <div>
          <div className="mb-4">
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <BookOpen size={16} />
                <span>Toplam: {totalLessons} ders</span>
              </div>
              <div className="flex items-center space-x-1">
                <Users size={16} />
                <span>Sınıf: {uniqueClasses.size}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar size={16} />
                <span>Gün: {Object.keys(lessonsByDay).length}</span>
              </div>
            </div>
          </div>

          {loadingDetailSchedule ? (
            <div className="text-center py-8 text-gray-500">
              <RefreshCw size={32} className="animate-spin mx-auto mb-2 text-blue-500" />
              <p className="text-sm">Ders programı yükleniyor...</p>
            </div>
          ) : totalLessons === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BookOpen size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Bu öğretmene henüz ders ataması yapılmamış.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <h4 className="text-base font-semibold text-gray-900 mb-3">Haftalık Program</h4>

              {days.map((day) => {
                const dayLessons = lessonsByDay[day] || [];

                return (
                  <div key={day} className="border border-gray-200 rounded-lg">
                    <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                      <h5 className="text-sm font-medium text-gray-900 flex items-center space-x-2">
                        <Calendar size={14} />
                        <span>{day}</span>
                        <span className="text-xs text-gray-500">({dayLessons.length})</span>
                      </h5>
                    </div>

                    {dayLessons.length === 0 ? (
                      <div className="p-3 text-center text-gray-400 text-xs">Ders yok</div>
                    ) : (
                      <div className="p-2">
                        <div className="space-y-2">
                          {dayLessons.map((entry: any) => {
                            const timeSlot = timeSlots.find((t) => t.id === String(entry.period));
                            const lessonName = entry.lesson?.name || entry.lesson?.lessonName || 'Ders';
                            const className = entry.class?.name || 'Sınıf';

                            return (
                              <div key={entry.id} className="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="text-blue-900 font-medium">{entry.period}. Saat</div>
                                  <div className="text-blue-700">
                                    {timeSlot ? `${timeSlot.startTime}-${timeSlot.endTime}` : ''}
                                  </div>
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{lessonName}</div>
                                  <div className="text-gray-600">{className} Sınıfı</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    );
  };

  if (showClassSchedule) {
    return renderClassSchedule();
  }

  if (showClassroomSchedule) {
    return renderClassroomSchedule();
  }

  if (showAllTeacherSchedules) {
    return renderAllTeacherSchedules();
  }

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col">
          {!hidePageHeading && (
            <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-4 sm:px-6">
              <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Öğretmen müsaitlikleri ve dağıtım</h2>
            </div>
          )}
          {renderScheduleViewSwitcher(
            getCurrentScheduleView(),
            <>
              <button
                type="button"
                onClick={autoDistributeSchedule}
                disabled={isDistributing}
                className={SCHED_BTN_INDIGO}>
                <Users size={16} className={`shrink-0 ${isDistributing ? "animate-pulse" : ""}`} />
                <span>{isDistributing ? "Dağıtılıyor..." : "Tüm öğretmenleri dağıt"}</span>
              </button>
              <button
                type="button"
                onClick={handleClearScheduleClick}
                disabled={isClearing}
                className={SCHED_BTN_DANGER}>
                <Trash2 size={16} className={`shrink-0 ${isClearing ? "animate-pulse" : ""}`} />
                <span>{isClearing ? "Sıfırlanıyor..." : "Dağıtımı sıfırla"}</span>
              </button>
            </>,
          )}
        </div>

        <div className="p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Öğretmenler</h3>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Öğretmen ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {loadingTeachers && (
                  <div className="text-center py-8 text-gray-500">
                    <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                    <p className="text-sm">Öğretmenler yükleniyor...</p>
                  </div>
                )}

                {!loadingTeachers && !teacherDeptId && (
                  <div className="text-center py-8 text-gray-500">
                    <GraduationCap size={32} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Öğretmen departmanı seçilmedi.</p>
                    <p className="text-xs mt-1">Planlama Ayarlarından öğretmen departmanı belirleyin.</p>
                  </div>
                )}

                {!loadingTeachers && teacherDeptId && teachers.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <GraduationCap size={32} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Öğretmen bulunamadı.</p>
                  </div>
                )}

                {!loadingTeachers && filteredTeachers.map((teacher) => (
                  <div
                    key={teacher.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors border ${selectedTeacher === teacher.id
                      ? "bg-blue-100 border-blue-200 text-blue-800"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                    onClick={() => setSelectedTeacher(teacher.id)}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <GraduationCap size={16} className="text-gray-500" />
                          <span className="font-medium text-sm">{teacher.name}</span>
                        </div>
                        <div className="text-xs text-gray-600 ml-6">{teacher.branch}</div>
                        <div className="text-xs text-green-600 ml-6 mt-1">Müsait: {selectedTeacher === teacher.id ? getTeacherAvailableHours(teacher.id) : teacher.availableHours} saat</div>
                        <div className="text-xs text-blue-600 ml-6">Ders: {teacher.assignedLessons} adet</div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openTeacherDetail(teacher.id);
                        }}
                        className="p-1 hover:bg-blue-200 rounded transition-colors"
                        title="Ders detaylarını görüntüle">
                        <Eye size={16} className="text-blue-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">{renderScheduleGrid()}</div>
        </div>
        </div>
      </div>

      {showTeacherDetail && renderTeacherDetailModal()}

      <ConfirmModal
        isOpen={clearScheduleModalOpen}
        onClose={() => setClearScheduleModalOpen(false)}
        onConfirm={confirmClearSchedule}
        title="Dağıtımı Sıfırla"
        message="Tüm ders programı dağılımını sıfırlamak istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmText="Sıfırla"
        cancelText="İptal"
        confirmButtonVariant="danger"
        isLoading={isClearing}
      />

      <ConfirmModal
        isOpen={successModalOpen}
        onClose={handleSuccessConfirm}
        onConfirm={handleSuccessConfirm}
        title="Başarılı"
        message={successMessage}
        confirmText="Tamam"
        cancelText=""
        confirmButtonVariant="primary"
      />

      <ConfirmModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        onConfirm={() => setErrorModalOpen(false)}
        title="Hata"
        message={errorMessage}
        confirmText="Tamam"
        cancelText=""
        confirmButtonVariant="primary"
      />

      <ConfirmModal
        isOpen={distributionResultModalOpen}
        onClose={() => setDistributionResultModalOpen(false)}
        onConfirm={handleDistributionResultConfirm}
        title="Otomatik Dağıtım Tamamlandı"
        message={distributionResultMessage}
        confirmText="Programları Görüntüle"
        cancelText="Kapat"
        confirmButtonVariant="primary"
      />
    </div>
  );
};

export default TeacherSchedule;